import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Tags,
  Plus,
  Edit2,
  Trash2,
  Sparkles,
  Users,
  Shield,
  GripVertical,
  ExternalLink,
} from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { useCanDefineAttributes } from '../../../hooks/prioritization/useCanDefineAttributes';
import type { Attribute } from '../../../types/prioritization';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { isObjectiveAttribute } from '../../../lib/objectiveCriteria';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { PRIORITIZATION_TOOLS, applyToolToAttributes, generateAttributeKeys } from '../../../data/prioritization/tools';
import type { PrioritizationTool } from '../../../types/prioritization/tools';
import { roleDefinitions } from '../../../types/prioritization/roles';

export function AttributeManagement() {
  const attributes = useAttributesStore((state) => state.attributes);
  const addAttribute = useAttributesStore((state) => state.addAttribute);
  const updateAttribute = useAttributesStore((state) => state.updateAttribute);
  const deleteAttribute = useAttributesStore((state) => state.deleteAttribute);
  const reorderAttributes = useAttributesStore((state) => state.reorderAttributes);
  const addNotification = useUIStore((state) => state.addNotification);

  const navigate = useNavigate();

  // Scoring attribute form state
  const [isAddingAttribute, setIsAddingAttribute] = useState(false);
  const [editingAttributeKey, setEditingAttributeKey] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Attribute>>({});
  const [newAttribute, setNewAttribute] = useState<Partial<Attribute>>({
    type: 'text',
    required: false,
  });
  const [showToolSelector, setShowToolSelector] = useState(false);
  const [selectedTool, setSelectedTool] = useState<PrioritizationTool | null>(null);

  const canEdit = useCanDefineAttributes();

  // ── Scoring attribute handlers ─────────────────────────────────────────────

  const handleAddAttribute = async () => {
    if (!newAttribute.key || !newAttribute.label) {
      addNotification({
        type: 'error',
        title: 'Missing Fields',
        message: 'Please provide both key and label for the attribute',
        duration: 3000,
      });
      return;
    }
    try {
      const attribute: Attribute = {
        key: newAttribute.key.toLowerCase().replace(/\s+/g, '-'),
        label: newAttribute.label,
        type: newAttribute.type || 'text',
        required: newAttribute.required || false,
        options: newAttribute.options,
        scope: 'global',
        inheritable: true,
        overridable: true,
      };
      const result = await addAttribute(attribute);
      if (!result?.error) {
        setNewAttribute({ type: 'text', required: false });
        setIsAddingAttribute(false);
        addNotification({
          type: 'success',
          title: 'Attribute Added',
          message: `"${attribute.label}" has been added to the schema`,
          duration: 3000,
        });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: (error instanceof Error && error.message) || 'Failed to add attribute',
        duration: 3000,
      });
    }
  };

  const handleDeleteAttribute = async (key: string) => {
    try {
      const result = await deleteAttribute(key);
      if (!result?.error) {
        addNotification({
          type: 'success',
          title: 'Attribute Deleted',
          message: 'Attribute removed from schema',
          duration: 3000,
        });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: (error instanceof Error && error.message) || 'Failed to delete attribute',
        duration: 3000,
      });
    }
  };

  const handleStartEdit = (attr: Attribute) => {
    setEditingAttributeKey(attr.key);
    setEditFormData({ label: attr.label, type: attr.type, required: attr.required, options: attr.options });
  };

  const handleCancelEdit = () => {
    setEditingAttributeKey(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    if (!editingAttributeKey) return;
    try {
      const result = await updateAttribute(editingAttributeKey, editFormData);
      if (!result?.error) {
        addNotification({
          type: 'success',
          title: 'Attribute Updated',
          message: `"${editFormData.label}" has been updated`,
          duration: 3000,
        });
        setEditingAttributeKey(null);
        setEditFormData({});
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: (error instanceof Error && error.message) || 'Failed to update attribute',
        duration: 3000,
      });
    }
  };

  const handleApplyTool = async (tool: PrioritizationTool) => {
    const result = applyToolToAttributes(tool, attributes);
    const keys = generateAttributeKeys(tool);
    let createdCount = 0;
    let alreadyExistedCount = result.existingKeys.length;
    const errors: string[] = [];

    for (let index = 0; index < tool.requiredAttributes.length; index++) {
      const attrDef = tool.requiredAttributes[index];
      const key = keys[index];
      if (result.existingKeys.includes(key.toLowerCase())) continue;
      try {
        const outcome = await addAttribute({
          key,
          label: attrDef.label,
          type: attrDef.type,
          required: attrDef.required || false,
          options: attrDef.options,
          validation: attrDef.validation,
          scope: 'global',
          inheritable: true,
          overridable: true,
        }) as { error?: unknown; alreadyExists?: boolean } | undefined;
        if (outcome?.alreadyExists) {
          alreadyExistedCount++;
        } else if (outcome?.error) {
          errors.push(`${attrDef.label}: ${typeof outcome.error === 'object' ? (outcome.error as { message?: string }).message || JSON.stringify(outcome.error) : String(outcome.error)}`);
        } else {
          createdCount++;
        }
      } catch (error) {
        errors.push(`${attrDef.label}: ${(error instanceof Error && error.message) || 'Failed to create'}`);
      }
    }

    if (createdCount > 0) {
      addNotification({
        type: 'success',
        title: 'Tool Applied',
        message: `Created ${createdCount} attribute${createdCount > 1 ? 's' : ''} from ${tool.name}.${alreadyExistedCount > 0 ? ` ${alreadyExistedCount} already existed.` : ''}`,
        duration: 5000,
      });
    }
    if (errors.length > 0) {
      addNotification({ type: 'error', title: 'Some Attributes Failed', message: errors.join('; '), duration: 5000 });
    }
    if (createdCount === 0 && errors.length === 0) {
      addNotification({ type: 'info', title: 'Tool Already Applied', message: `All attributes from ${tool.name} already exist in your schema.`, duration: 5000 });
    }
    setShowToolSelector(false);
    setSelectedTool(null);
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  // A strategic objective used as a criterion (1066) is named and kept by its objective, not here.
  const scoringAttributes = attributes.filter(a => !a.isCustomField && !isObjectiveAttribute(a));

  // ── Drag-and-drop reordering ───────────────────────────────────────────────

  const [localOrder, setLocalOrder] = useState<string[]>(() => scoringAttributes.map(a => a.key));
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // Keep local order in sync when attributes change from the store
  const storeOrderKey = scoringAttributes.map(a => a.key).join(',');
  useEffect(() => {
    setLocalOrder(scoringAttributes.map(a => a.key));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeOrderKey]);

  const orderedScoringAttributes = useMemo(() => {
    const byKey = Object.fromEntries(scoringAttributes.map(a => [a.key, a]));
    return localOrder.map(k => byKey[k]).filter(Boolean);
  }, [localOrder, scoringAttributes]);

  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    setDraggedKey(key);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, key: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== draggedKey) setDragOverKey(key);
  }, [draggedKey]);

  const handleDrop = useCallback((e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    if (!draggedKey || draggedKey === targetKey) {
      setDraggedKey(null);
      setDragOverKey(null);
      return;
    }
    const newOrder = [...localOrder];
    const fromIdx = newOrder.indexOf(draggedKey);
    const toIdx = newOrder.indexOf(targetKey);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedKey);
    setLocalOrder(newOrder);
    setDraggedKey(null);
    setDragOverKey(null);
    void reorderAttributes(newOrder);
  }, [draggedKey, localOrder, reorderAttributes]);

  const handleDragEnd = useCallback(() => {
    setDraggedKey(null);
    setDragOverKey(null);
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Tags className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Attribute Management"
        subtitle="Scoring attributes for prioritization sessions. For extra fields on item records, use Settings → Custom Fields."
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600 dark:text-[var(--text-sec)]">Scoring Attributes</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-[var(--text-pri)]">{scoringAttributes.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 dark:text-[var(--text-sec)]">Required</p>
          <p className="text-3xl font-bold text-red-600 dark:text-red-400">{scoringAttributes.filter(a => a.required).length}</p>
        </div>
      </div>

      {/* ── Prioritization Tools ── */}
      {canEdit && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                Prioritization Tools
              </h2>
              <p className="text-sm text-blue-700 mt-1">
                Apply predefined tools to quickly set up attribute schemas (RICE, ICE, WSJF, etc.)
              </p>
            </div>
            {!showToolSelector && (
              <button onClick={() => setShowToolSelector(true)} className="btn btn-primary flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Apply Tool
              </button>
            )}
          </div>

          {showToolSelector && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PRIORITIZATION_TOOLS.map((tool) => {
                  const result = applyToolToAttributes(tool, attributes);
                  const alreadyApplied = result.existingKeys.length === tool.requiredAttributes.length;
                  return (
                    <div
                      key={tool.id}
                      className={`p-4 rounded-lg border-2 ${
                        selectedTool?.id === tool.id ? 'border-blue-500 bg-blue-100' : 'border-blue-200 bg-white hover:border-blue-300'
                      } cursor-pointer transition-colors`}
                      onClick={() => setSelectedTool(tool)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{tool.name}</h3>
                          <p className="text-xs text-gray-600 mt-0.5">{tool.description}</p>
                        </div>
                        {alreadyApplied && (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Applied</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{tool.category}</span>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{tool.complexity}</span>
                        <span className="text-gray-500">{tool.requiredAttributes.length} attributes</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedTool && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">{selectedTool.name}</h3>
                    <button onClick={() => setSelectedTool(null)} className="text-gray-400 hover:text-gray-600">✕</button>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{selectedTool.description}</p>
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-700 mb-2">Attributes this tool will create:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedTool.requiredAttributes.map((attr, idx) => {
                        const keys = generateAttributeKeys(selectedTool);
                        const key = keys[idx];
                        const exists = attributes.some(a => a.key.toLowerCase() === key.toLowerCase());
                        return (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className={`px-2 py-1 rounded ${exists ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {attr.label}
                            </span>
                            {exists && <span className="text-green-600">✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-700 mb-2">When to use:</p>
                    <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                      {selectedTool.whenToUse.map((use, idx) => <li key={idx}>{use}</li>)}
                    </ul>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button onClick={() => { setShowToolSelector(false); setSelectedTool(null); }} className="btn btn-secondary">Cancel</button>
                    <button onClick={() => handleApplyTool(selectedTool)} className="btn btn-primary">Apply {selectedTool.name}</button>
                  </div>
                </div>
              )}

              {!selectedTool && (
                <button onClick={() => setShowToolSelector(false)} className="btn btn-secondary w-full">Cancel</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Scoring Attributes ── */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-[var(--text-pri)]">Scoring Attributes</h2>
            <p className="text-sm text-gray-500 dark:text-[var(--text-sec)] mt-0.5">Used in prioritization sessions to evaluate and rank items.</p>
          </div>
          {canEdit && (
            <button onClick={() => setIsAddingAttribute(true)} className="btn btn-primary flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Attribute
            </button>
          )}
        </div>

        {isAddingAttribute && canEdit && (
          <div className="mb-6 p-4 bg-purple-50 dark:bg-indigo-950/40 rounded-lg border border-purple-200 dark:border-indigo-800/50">
            <h3 className="font-medium text-gray-900 dark:text-[var(--text-pri)] mb-3">New Attribute</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key (Internal) *</label>
                <input
                  type="text"
                  value={newAttribute.key || ''}
                  onChange={(e) => setNewAttribute({ ...newAttribute, key: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="e.g., customer-impact"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[var(--rule)] rounded-md dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label (Display) *</label>
                <input
                  type="text"
                  value={newAttribute.label || ''}
                  onChange={(e) => setNewAttribute({ ...newAttribute, label: e.target.value })}
                  placeholder="e.g., Customer Impact"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[var(--rule)] rounded-md dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newAttribute.required || false}
                    onChange={(e) => setNewAttribute({ ...newAttribute, required: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Required field</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-gray-500">
                  After creating, use{' '}
                  <span className="font-medium text-gray-700">Settings → Attribute Permissions</span> for who can set values,{' '}
                  <span className="font-medium text-gray-700">Value Scales</span> for measurement scales, or click the attribute name for the full attribute page.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { setIsAddingAttribute(false); setNewAttribute({ type: 'text', required: false }); }} className="btn btn-secondary">Cancel</button>
              <button onClick={handleAddAttribute} className="btn btn-primary" disabled={!newAttribute.key || !newAttribute.label}>Add Attribute</button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-[var(--surface)] border-b border-gray-200 dark:border-[var(--rule)]">
              <tr>
                {canEdit && <th className="px-2 py-3 w-8" />}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[var(--text-sec)] uppercase">Key</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[var(--text-sec)] uppercase">Label</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[var(--text-sec)] uppercase">Required</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[var(--text-sec)] uppercase">Who Can Set Values</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-[var(--text-sec)] uppercase">Scales</th>
                {canEdit && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-[var(--text-sec)] uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[var(--rule)]">
              {orderedScoringAttributes.map((attr) =>
                editingAttributeKey === attr.key ? (
                  <tr
                    key={attr.key}
                    className="bg-purple-50 dark:bg-indigo-950/45 dark:ring-1 dark:ring-inset dark:ring-indigo-500/25"
                  >
                    {canEdit && <td className="px-2 py-3 w-8" />}
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-[var(--text-pri)]">{attr.key}</td>
                    <td className="px-4 py-3 text-sm">
                      <input
                        type="text"
                        value={editFormData.label || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-[var(--rule)] rounded text-sm dark:bg-neutral-950/90 dark:text-[var(--text-pri)]"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editFormData.required || false}
                          onChange={(e) => setEditFormData({ ...editFormData, required: e.target.checked })}
                          className="rounded border-gray-300 dark:border-[var(--rule)]"
                        />
                        <span className="text-xs text-gray-600 dark:text-[var(--text-sec)]">Required</span>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-sm align-top">
                      <Link
                        to={`/prioritization/settings/attribute-permissions/${encodeURIComponent(attr.key)}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-indigo-400 hover:text-primary-800 dark:hover:text-indigo-300 hover:underline"
                      >
                        Who can set values (governance)
                        <ExternalLink className="h-3 w-3 opacity-70" />
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm align-top">
                      <Link
                        to={`/prioritization/settings/value-scales/${encodeURIComponent(attr.key)}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-indigo-400 hover:text-primary-800 dark:hover:text-indigo-300 hover:underline"
                      >
                        Measurement scales
                        <ExternalLink className="h-3 w-3 opacity-70" />
                      </Link>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={handleSaveEdit} className="px-2 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700">Save</button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-2 py-1 bg-gray-200 text-gray-700 dark:bg-neutral-700 dark:text-neutral-100 rounded text-xs hover:bg-gray-300 dark:hover:bg-neutral-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ) : (
                  <tr
                    key={attr.key}
                    draggable={canEdit}
                    onDragStart={canEdit ? (e) => handleDragStart(e, attr.key) : undefined}
                    onDragOver={canEdit ? (e) => handleDragOver(e, attr.key) : undefined}
                    onDrop={canEdit ? (e) => handleDrop(e, attr.key) : undefined}
                    onDragEnd={canEdit ? handleDragEnd : undefined}
                    className={`transition-colors ${
                      draggedKey === attr.key
                        ? 'opacity-40 bg-gray-50 dark:bg-[var(--surface)]'
                        : dragOverKey === attr.key
                        ? 'bg-blue-50 dark:bg-sky-950/40 border-t-2 border-t-blue-400 dark:border-t-sky-500'
                        : 'hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]'
                    }`}
                  >
                    {canEdit && (
                      <td className="px-2 py-3 w-8">
                        <GripVertical className="h-4 w-4 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing" />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-[var(--text-pri)]">{attr.key}</td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => navigate(`/prioritization/settings/attributes/${attr.key}`)}
                        className="text-gray-900 dark:text-[var(--text-pri)] hover:text-primary-600 dark:hover:text-primary-400 hover:underline font-medium text-left"
                      >
                        {attr.label}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {attr.required ? <span className="text-red-600 font-medium">Required</span> : <span className="text-gray-500">Optional</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Link
                        to={`/prioritization/settings/attribute-permissions/${encodeURIComponent(attr.key)}`}
                        className="block text-left hover:opacity-90"
                      >
                        {attr.governance ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-blue-500" />
                              <span className="text-xs text-blue-700">
                                {attr.governance.valueProposerRoles.map(role => roleDefinitions[role]?.label || role).slice(0, 2).join(', ')}
                                {attr.governance.valueProposerRoles.length > 2 && ` +${attr.governance.valueProposerRoles.length - 2}`}
                              </span>
                            </div>
                            {attr.governance.valueApproverRoles?.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Shield className="h-3 w-3 text-green-500" />
                                <span className="text-xs text-green-700">
                                  {attr.governance.valueApproverRoles.map(role => roleDefinitions[role]?.label || role).slice(0, 2).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : attr.proposerType ? (
                          <span className="text-xs text-gray-500 italic">{attr.proposerType}</span>
                        ) : (
                          <span className="text-xs text-gray-400">Any role</span>
                        )}
                        <span className="sr-only"> — open attribute governance</span>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(attr.scales?.length ?? 0) > 0 ? (
                        <Link
                          to={`/prioritization/settings/value-scales/${encodeURIComponent(attr.key)}`}
                          className="text-xs text-primary-600 hover:text-primary-800 hover:underline"
                        >
                          {attr.scales?.length} scale{attr.scales?.length !== 1 ? 's' : ''}
                        </Link>
                      ) : (
                        <Link
                          to={`/prioritization/settings/value-scales/${encodeURIComponent(attr.key)}`}
                          className="text-xs text-primary-600 hover:text-primary-800 hover:underline"
                        >
                          Add scales
                        </Link>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleStartEdit(attr)} className="text-gray-400 hover:text-primary-600" title="Edit attribute">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAttribute(attr.key)}
                            className="text-gray-400 hover:text-red-600"
                            title={attr.required ? 'Make optional before deleting' : 'Delete attribute'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Help Text ── */}
      <div className="card bg-purple-50 border-purple-200">
        <h3 className="font-medium text-purple-900 mb-2">About Attributes</h3>
        <p className="text-sm text-purple-800">
          Attributes are the fields used to describe and evaluate items. They become the building blocks
          for evaluation criteria when ranking items in Candidate Pools.
        </p>
        <p className="mt-2 text-sm text-purple-800">
          <strong>Who can set values</strong> opens per-attribute governance (same as Settings → Attribute Permissions).{' '}
          <strong>Scales</strong> opens the measurement-scale editor. The attribute name still opens the full attribute page (label, type, scales, and inheritance).
        </p>
        <p className="mt-2 text-sm text-purple-800">
          <strong>Custom fields</strong> on items (not scoring criteria) are managed under{' '}
          <Link to="/prioritization/settings/custom-fields" className="font-medium text-purple-900 underline hover:text-purple-950">
            Settings → Custom Fields
          </Link>
          .
        </p>
        <div className="mt-3 text-xs text-purple-700">
          <p className="font-medium mb-1">Measurement Scales:</p>
          <p className="mb-1">Each attribute can have multiple measurement scales. When configuring criteria in a cycle or track, you choose which scale to use.</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong>Numeric:</strong> Range-based (e.g., 1–5, 1–10)</li>
            <li><strong>Ordinal:</strong> Labeled levels (e.g., Low / Medium / High, T-Shirt Sizes)</li>
            <li><strong>Fibonacci:</strong> Planning Poker-style (1, 2, 3, 5, 8, 13, 21)</li>
            <li><strong>Binary:</strong> Yes / No</li>
            <li><strong>Custom:</strong> Any set of labeled values</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
